"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-10">
        <div className="h-5 w-20 bg-muted animate-pulse rounded-sm" />
        <div className="h-5 w-20 bg-muted animate-pulse rounded-sm" />
        <div className="h-5 w-20 bg-muted animate-pulse rounded-sm" />
        <div className="h-5 w-20 bg-muted animate-pulse rounded-sm" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6 lg:gap-10">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className="bg-transparent hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent data-[active=true]:hover:bg-transparent data-[active=true]:focus:bg-transparent px-0 py-0">
              <Link to={item.href || "#"} className="relative text-foreground hover:text-accent transition-colors duration-200 text-[15px] font-default font-medium tracking-[-0.005em] py-2 group">
                {item.label}
                <span className="absolute left-1/2 -bottom-0.5 h-px w-0 bg-accent transition-all duration-[280ms] ease-out group-hover:w-full group-hover:left-0" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6">
        <div className="h-8 w-3/4 bg-muted animate-pulse rounded-sm" />
        <div className="h-8 w-2/3 bg-muted animate-pulse rounded-sm" />
        <div className="h-8 w-3/4 bg-muted animate-pulse rounded-sm" />
        <div className="h-8 w-1/2 bg-muted animate-pulse rounded-sm" />
      </div>;
  }
  return <nav className="flex flex-col">
      {menuItems.map((item, i) => <div data-index={i} key={item.id} className="border-b border-border last:border-b-0">
          <SheetClose asChild>
            <Link to={item.href || "#"} className="block py-5 text-foreground hover:text-accent transition-colors duration-200 text-2xl font-default font-normal tracking-[-0.015em] leading-tight">
              {item.label}
            </Link>
          </SheetClose>
        </div>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="11429" id="header" className={`sticky top-0 left-0 right-0 z-50 w-full transition-all duration-[250ms] ease-out bg-background ${scrolled ? "backdrop-blur-lg border-b border-border" : "border-b border-transparent"}`}>
      <div className="mx-auto max-w-[1280px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 items-center h-16 md:h-20 gap-4">
          <div className="col-span-6 md:col-span-3 flex items-center">
            <Link to="/HomePage" className="inline-flex items-center" aria-label="MetPro Partners home">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1705670/img/log3.jpg" alt="MetPro Partners" className="h-7 !w-[115.844px] !h-[61px]" loading="lazy" />
            </Link>
          </div>

          <div className="hidden md:flex md:col-span-6 items-center justify-center">
            <MenuProvider menu_id="4" className="header-menu">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          <div className="hidden md:flex md:col-span-3 items-center justify-end">
            <Button asChild className="h-11 px-7 bg-accent text-accent-foreground hover:bg-accent/90 transition-colors duration-[240ms] ease-out text-sm font-default font-bold tracking-[0.02em]">
              <Link to="/ContactPage">Request Sales</Link>
            </Button>
          </div>

          <div className="col-span-6 flex md:hidden items-center justify-end">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu" className="text-foreground hover:bg-transparent hover:text-accent h-10 w-10">
                  <Menu className="size-6" strokeWidth={1.5} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background border-l border-border w-full sm:max-w-sm p-0 flex flex-col">
                <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
                  <SheetTitle asChild>
                    <Link to="/HomePage" className="inline-flex items-center" aria-label="MetPro Partners home">
                      <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1705670/img/log3.jpg" alt="MetPro Partners" className="h-7" loading="lazy" />
                    </Link>
                  </SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                  <MenuProvider menu_id="4" className="mobile-header-menu">
                    <MobileMenuConsumer />
                  </MenuProvider>
                </div>

                <div className="px-6 py-6 border-t border-border">
                  <SheetClose asChild>
                    <Button asChild className="w-full h-12 bg-accent text-accent-foreground hover:bg-accent/90 transition-colors duration-[240ms] ease-out text-sm font-default font-bold tracking-[0.02em]">
                      <Link to="/ContactPage">Request Counsel</Link>
                    </Button>
                  </SheetClose>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}